import type { IEmailCampaignDesignStatus } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilderTypes";
import type { TBadgeType } from "components/badge/Badge";
import { emailDocumentsEqual } from "pages/secure/facility/marketing/emailBuilder/emailBuilderAutosaveBaseline";
import type { EmailDocument } from "pages/secure/facility/marketing/emailBuilder/domain/types";
import { resolveEmailCampaignDesignDisplayStatus } from "pages/secure/facility/marketing/emailCampaigns/emailCampaignDesignStatus";
import type {
  IEmailBuilderTemplateActivation,
  TEmailBuilderAutoSaveStatus,
} from "redux/reducers/emailBuilder/emailBuilder";
import { EMAIL_BUILDER_AUTOSAVE_DEBOUNCE_MS, EMAIL_BUILDER_AUTOSAVE_CONFLICT_LABEL } from "redux/reducers/emailBuilder/emailBuilderAutosave";

export function emailBuilderAutosaveDueAt(nowMs: number): number {
  return nowMs + EMAIL_BUILDER_AUTOSAVE_DEBOUNCE_MS;
}

export function emailBuilderUnsavedCountdownSeconds(dueAtMs: number, nowMs: number): number {
  return Math.max(0, Math.ceil((dueAtMs - nowMs) / 1000));
}

export type TEmailBuilderSavedDraftLabelKind = "draft" | "published" | "unpublished-changes";

export function emailBuilderSavedDraftLabelKind(
  design: IEmailCampaignDesignStatus | null | undefined,
  draftRevision: number | null | undefined,
): TEmailBuilderSavedDraftLabelKind {
  if (design?.active_published_design_id == null) {
    return "draft";
  }

  switch (resolveEmailCampaignDesignDisplayStatus(design, draftRevision)) {
    case "unpublished_changes":
      return "unpublished-changes";
    case "current":
      return "published";
    case "not_designed":
    default:
      return "draft";
  }
}

export function emailBuilderTemplateStatusKind(
  templateActivation: IEmailBuilderTemplateActivation | null | undefined,
  draftRevision: number | null | undefined,
  document?: EmailDocument | null,
): TEmailBuilderSavedDraftLabelKind {
  if (templateActivation == null || templateActivation.activatedAt == null) {
    return "draft";
  }

  if (draftRevision == null) {
    return "draft";
  }

  if (draftRevision === templateActivation.activeSourceDraftRevision) {
    return "published";
  }

  if (
    document != null &&
    templateActivation.activeDocument != null &&
    emailDocumentsEqual(document, templateActivation.activeDocument)
  ) {
    return "published";
  }

  return "unpublished-changes";
}

function resolveEmailBuilderSavedStatusKind(options?: {
  design?: IEmailCampaignDesignStatus | null;
  draftRevision?: number | null;
  templateActivation?: IEmailBuilderTemplateActivation | null;
  document?: EmailDocument | null;
}): TEmailBuilderSavedDraftLabelKind {
  if (options?.templateActivation !== undefined) {
    return emailBuilderTemplateStatusKind(
      options.templateActivation,
      options?.draftRevision,
      options?.document,
    );
  }

  return emailBuilderSavedDraftLabelKind(options?.design, options?.draftRevision ?? null);
}

export function emailBuilderDraftStatusLabel(
  autoSaveStatus: TEmailBuilderAutoSaveStatus,
  options?: {
    dueAt?: number | null;
    now?: number;
    design?: IEmailCampaignDesignStatus | null;
    draftRevision?: number | null;
    templateActivation?: IEmailBuilderTemplateActivation | null;
    document?: EmailDocument | null;
    conflict?: boolean;
  },
): string {
  if (options?.conflict) {
    return EMAIL_BUILDER_AUTOSAVE_CONFLICT_LABEL;
  }

  switch (autoSaveStatus) {
    case "saving":
      return "Saving…";
    case "unsaved-changes":
      return "Unsaved changes";
    case "error":
      return "Error saving";
    case "saved":
    default: {
      const isTemplate = options?.templateActivation !== undefined;
      switch (resolveEmailBuilderSavedStatusKind(options)) {
        case "published":
          return isTemplate ? "Active" : "Finalized";
        case "unpublished-changes":
          return isTemplate ? "Draft changes" : "Unfinalized changes";
        case "draft":
        default:
          return "Draft";
      }
    }
  }
}

export function emailBuilderDraftStatusBadgeType(
  autoSaveStatus: TEmailBuilderAutoSaveStatus,
  options?: {
    design?: IEmailCampaignDesignStatus | null;
    draftRevision?: number | null;
    templateActivation?: IEmailBuilderTemplateActivation | null;
    document?: EmailDocument | null;
  },
): TBadgeType {
  if (autoSaveStatus === "error") {
    return "error";
  }

  if (autoSaveStatus === "saved") {
    switch (resolveEmailBuilderSavedStatusKind(options)) {
      case "published":
        return "success";
      case "unpublished-changes":
        return "warning";
      case "draft":
      default:
        return "gray";
    }
  }

  return "gray";
}

export function emailBuilderDraftStatusAnnouncement(
  autoSaveStatus: TEmailBuilderAutoSaveStatus,
  autoSaveError: string | null = null,
  options?: {
    design?: IEmailCampaignDesignStatus | null;
    draftRevision?: number | null;
    templateActivation?: IEmailBuilderTemplateActivation | null;
    document?: EmailDocument | null;
  },
): string {
  switch (autoSaveStatus) {
    case "unsaved-changes":
      return "Unsaved changes";
    case "saving":
      return "Saving";
    case "error":
      return autoSaveError !== null && autoSaveError.length > 0 ? autoSaveError : "Error saving";
    case "saved":
    default: {
      const isTemplate = options?.templateActivation !== undefined;
      switch (resolveEmailBuilderSavedStatusKind(options)) {
        case "published":
          return isTemplate ? "Active" : "Finalized";
        case "unpublished-changes":
          return isTemplate ? "Draft changes saved" : "Unfinalized changes saved";
        case "draft":
        default:
          return "Draft saved";
      }
    }
  }
}
